import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Building2, User, Shield } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [activeTab, setActiveTab] = useState('tenant');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(loginForm.email, loginForm.password);
    if (result.success) {
      const role = result.data.role;
      if (role === 'admin') navigate('/dashboard/admin');
      else if (role === 'landlord') navigate('/dashboard/landlord');
      else navigate('/dashboard/tenant');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    const role = activeTab === 'landlord' ? 'landlord' : 'tenant';
    const result = await register(registerForm.name, registerForm.email, registerForm.password, role);
    if (result.success) {
      if (role === 'landlord') navigate('/dashboard/landlord');
      else navigate('/dashboard/tenant');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 pt-20 pb-16" data-testid="login-page">
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-emerald-900 transition-colors mb-8 text-sm" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </button>

        <div className="text-center mb-8">
          <span className="font-outfit font-black text-emerald-900 text-3xl tracking-tighter">
            4L<span className="text-emerald-500">.</span>
          </span>
          <p className="font-outfit text-stone-600 mt-2">
            {mode === 'login' ? 'Sign in to your account' : 'Create a new account'}
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          {/* Portal Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid grid-cols-3 h-auto bg-stone-100 rounded-xl p-1">
              <TabsTrigger value="tenant" className="rounded-lg py-2 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-tenant">
                <User className="w-3.5 h-3.5 mr-1" /> Tenant
              </TabsTrigger>
              <TabsTrigger value="landlord" className="rounded-lg py-2 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-landlord">
                <Building2 className="w-3.5 h-3.5 mr-1" /> Landlord
              </TabsTrigger>
              <TabsTrigger value="admin" className="rounded-lg py-2 text-xs font-medium data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm" data-testid="tab-admin">
                <Shield className="w-3.5 h-3.5 mr-1" /> Admin
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
              <p className="text-red-700 text-sm" data-testid="auth-error">{error}</p>
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  required
                  placeholder="you@example.com"
                  className="text-stone-900"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Password</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                  placeholder="Enter your password"
                  className="text-stone-900"
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full py-6 font-semibold shadow-lg shadow-emerald-900/20 transition-all"
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Full Name</Label>
                <Input
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  required
                  placeholder="John Doe"
                  className="text-stone-900"
                  data-testid="register-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                  required
                  placeholder="you@example.com"
                  className="text-stone-900"
                  data-testid="register-email"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Password</Label>
                <Input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  required
                  placeholder="Create a password"
                  className="text-stone-900"
                  data-testid="register-password"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-emerald-900/70 mb-2 block">Confirm Password</Label>
                <Input
                  type="password"
                  value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                  required
                  placeholder="Confirm your password"
                  className="text-stone-900"
                  data-testid="register-confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full py-6 font-semibold shadow-lg shadow-emerald-900/20 transition-all"
                data-testid="register-submit"
              >
                {loading ? 'Creating account...' : `Create ${activeTab === 'landlord' ? 'Landlord' : 'Tenant'} Account`}
              </Button>
            </form>
          )}

          {activeTab !== 'admin' && (
            <p className="text-center text-sm text-stone-500 mt-5">
              {mode === 'login' ? (
                <>Don't have an account?{' '}
                  <button onClick={() => { setMode('register'); setError(''); }} className="text-emerald-900 font-semibold hover:underline" data-testid="switch-to-register">
                    Sign up
                  </button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button onClick={() => { setMode('login'); setError(''); }} className="text-emerald-900 font-semibold hover:underline" data-testid="switch-to-login">
                    Sign in
                  </button>
                </>
              )}
            </p>
          )}
          {activeTab === 'admin' && mode !== 'login' && (
            <p className="text-center text-sm text-stone-500 mt-5">
              Admin accounts are created by existing admins.{' '}
              <button onClick={() => { setMode('login'); setError(''); }} className="text-emerald-900 font-semibold hover:underline">
                Sign in instead
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
